import { HTMLAttributes, ReactNode, useEffect, useLayoutEffect, useRef } from 'react'
import './Dialog.less'
import './DialogAnimation.less'
import dialogPolyfill from 'dialog-polyfill'

interface DialogProps extends HTMLAttributes<HTMLDialogElement> {
  children: ReactNode
  isModal: boolean
  isOpen: boolean
  onClose?: () => void
}

const Dialog = ({ children, isModal, isOpen, onClose, ...htmlProps }: DialogProps): JSX.Element => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const dialog = dialogRef.current
  const dialogLoaded = dialog !== null

  useLayoutEffect(() => {
    if (!dialogLoaded) return

    dialogPolyfill.registerDialog(dialog)
  }, [dialogLoaded])

  useEffect(() => {
    if (!dialogLoaded) return

    if (isOpen && !dialog.open) {
      return isModal ? dialog.showModal() : dialog.show()
    }

    if (!isOpen && dialog.open) dialog.close()
  }, [dialogLoaded, isOpen])

  useEffect(() => {
    if (!dialogLoaded || !isOpen || !isModal || !onClose) return

    const handleOutsideClick = (event: MouseEvent) => {
      const { top, bottom, left, right } = dialog.getBoundingClientRect()
      const isInsideDialog =
        event.clientY >= top &&
        event.clientY <= bottom &&
        event.clientX >= left &&
        event.clientX <= right

      if (!isInsideDialog) onClose()
    }
    const timeoutId = window.setTimeout(() => {
      document.addEventListener('click', handleOutsideClick)
    }, 0)

    return () => {
      window.clearTimeout(timeoutId)
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [dialogLoaded, isOpen, isModal, onClose])

  return (
    <>
      <dialog className={`hb-dialog ${htmlProps.className}`} ref={dialogRef}>
        {children}
      </dialog>
    </>
  )
}

export default Dialog
